import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth/AuthContext";

const STORAGE_KEY = "reshet_area_manager_acting_store";
const STORAGE_NAME_KEY = "reshet_area_manager_acting_store_name";

const AreaManagerSessionContext = createContext(null);

function readStoredActingStore() {
  try {
    if (typeof window === "undefined") return { id: null, name: "" };
    const params = new URLSearchParams(window.location.search);
    return {
      id: params.get("storeId")?.trim() || null,
      name: params.get("storeName")?.trim() || "",
    };
  } catch {
    return { id: null, name: "" };
  }
}

function isAreaManagerUser(user) {
  const roleKey = user?.role?.key || user?.role;
  if (roleKey === "AREA_MANAGER") return true;
  const custom = String(user?.custom_role ?? "").trim();
  return custom === "area_manager" || custom === "AREA_MANAGER";
}

function managedStoreIds(user) {
  return (user?.managed_stores || [])
    .map((id) => String(id?._id ?? id ?? "").trim())
    .filter(Boolean);
}

export function AreaManagerSessionProvider({ children }) {
  const { user } = useAuth();
  const isAreaManager = isAreaManagerUser(user);

  const [stored, setStored] = useState(() => readStoredActingStore());

  useEffect(() => {
    if (!isAreaManager) {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_NAME_KEY);
      } catch (_) {}
      setStored({ id: null, name: "" });
      return;
    }
    if (!stored.id) return;
    const allowed = managedStoreIds(user);
    if (!allowed.includes(String(stored.id))) {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_NAME_KEY);
      } catch (_) {}
      setStored({ id: null, name: "" });
    }
  }, [user, isAreaManager, stored.id]);

  const enterStore = useCallback((store) => {
    const id = String(store?._id ?? store?.id ?? "").trim();
    if (!id) return;
    const name = String(store?.name ?? "").trim();
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_NAME_KEY);
    } catch (_) {}
    setStored({ id, name });
  }, []);

  const exitStore = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_NAME_KEY);
    } catch (_) {}
    setStored({ id: null, name: "" });
  }, []);

  const value = useMemo(
    () => ({
      isAreaManager,
      actingStoreId: isAreaManager ? stored.id : null,
      actingStoreName: isAreaManager ? stored.name : "",
      isActingAsStore: isAreaManager && !!stored.id,
      enterStore,
      exitStore,
    }),
    [isAreaManager, stored.id, stored.name, enterStore, exitStore]
  );

  return (
    <AreaManagerSessionContext.Provider value={value}>
      {children}
    </AreaManagerSessionContext.Provider>
  );
}

export function useAreaManagerSession() {
  const ctx = useContext(AreaManagerSessionContext);
  if (!ctx) {
    return {
      isAreaManager: false,
      actingStoreId: null,
      actingStoreName: "",
      isActingAsStore: false,
      enterStore: () => {},
      exitStore: () => {},
    };
  }
  return ctx;
}
